import React, { useState, useEffect, useCallback } from 'react';
import {
  Card,
  CardBody,
  CardHeader,
  Tabs,
  Tab,
  Spinner,
  Chip,
  Button,
  Table,
  TableHeader,
  TableColumn,
  TableBody,
  TableRow,
  TableCell,
  Tooltip,
} from '@nextui-org/react';
import {
  PieChart,
  Pie,
  Cell,
  ResponsiveContainer,
  Tooltip as RechartsTooltip,
} from 'recharts';
import { FaExclamationTriangle, FaInfoCircle } from 'react-icons/fa';
import { FaArrowsRotate } from 'react-icons/fa6';
import Icon from '../base/Icon';
import {
  apiKeyService,
  ErrorData,
  RecentError,
} from '../../services/apiKeyService';
import { DateRange } from './DateRangeSelector';

interface ErrorAnalysisCardProps {
  className?: string;
  dateRange?: DateRange;
  apiKeyId?: string;
}

const ErrorAnalysisCard: React.FC<ErrorAnalysisCardProps> = ({
  className = '',
  dateRange,
  apiKeyId,
}) => {
  const [errorData, setErrorData] = useState<ErrorData[]>([]);
  const [recentErrors, setRecentErrors] = useState<RecentError[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const loadErrorData = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      // Use date range if provided, otherwise use default
      const response = dateRange?.isAllTime
        ? await apiKeyService.getErrorAnalysis(
            30,
            undefined,
            undefined,
            true,
            apiKeyId
          ) // All time with proper flag
        : dateRange?.startDate && dateRange?.endDate && !dateRange.isAllTime
          ? await apiKeyService.getErrorAnalysis(
              30,
              dateRange.startDate,
              dateRange.endDate,
              false,
              apiKeyId
            )
          : await apiKeyService.getErrorAnalysis(
              30,
              undefined,
              undefined,
              false,
              apiKeyId
            );
      setErrorData(response.errors || []);
      setRecentErrors(response.recent_errors || []);
    } catch (err) {
      console.error('Failed to load error analysis data:', err);
      setError('Failed to load error analysis data');
      setErrorData([]);
      setRecentErrors([]);
    } finally {
      setLoading(false);
    }
  }, [dateRange, apiKeyId]);

  useEffect(() => {
    loadErrorData();
  }, [loadErrorData]);

  // Safe percentage conversion helper
  const safePercentage = (value: any): number => {
    if (typeof value === 'number' && !isNaN(value)) {
      return value;
    }
    if (typeof value === 'string') {
      const parsed = parseFloat(value);
      return isNaN(parsed) ? 0 : parsed;
    }
    return 0;
  };

  // Get HTTP status code info
  const getStatusCodeInfo = (statusCode: number) => {
    const statusCodes: {
      [key: number]: {
        name: string;
        description: string;
        color: 'danger' | 'warning' | 'default';
      };
    } = {
      400: {
        name: 'Bad Request',
        description: 'Invalid request format or parameters',
        color: 'warning',
      },
      401: {
        name: 'Unauthorized',
        description: 'Authentication failed or missing API key',
        color: 'danger',
      },
      403: {
        name: 'Forbidden',
        description: 'Access denied or insufficient permissions',
        color: 'danger',
      },
      404: {
        name: 'Not Found',
        description: 'Endpoint or resource not found',
        color: 'warning',
      },
      422: {
        name: 'Validation Error',
        description: 'Request data validation failed',
        color: 'warning',
      },
      429: {
        name: 'Rate Limited',
        description: 'Too many requests',
        color: 'warning',
      },
      500: {
        name: 'Server Error',
        description: 'Internal server error occurred',
        color: 'danger',
      },
      502: {
        name: 'Bad Gateway',
        description: 'Upstream service error',
        color: 'danger',
      },
      503: {
        name: 'Service Unavailable',
        description: 'Service temporarily unavailable',
        color: 'danger',
      },
      504: {
        name: 'Gateway Timeout',
        description: 'Upstream service timeout',
        color: 'danger',
      },
    };

    return (
      statusCodes[statusCode] || {
        name: `HTTP ${statusCode}`,
        description: 'Unknown error type',
        color: 'default' as const,
      }
    );
  };

  // Format endpoint names for better display
  const formatEndpoint = (endpoint: string) => {
    return endpoint
      .replace('/api/v1/', '')
      .replace('analyze/', '')
      .split('/')
      .map((part) =>
        part.replace(/_/g, ' ').replace(/\b\w/g, (l) => l.toUpperCase())
      )
      .join(' / ');
  };

  // Colors for pie chart
  const ERROR_COLORS = [
    '#ff7300',
    '#ff4444',
    '#ffaa00',
    '#ff6600',
    '#cc3300',
    '#ff9900',
  ];

  const renderErrorDistribution = () => {
    if (!errorData.length) {
      return (
        <div className="flex flex-col items-center justify-center h-64 text-gray-500">
          <div className="text-center">
            <p className="text-sm font-medium mb-2">No error data available</p>
            <p className="text-xs text-gray-400">
              Error statistics will appear once errors occur
            </p>
          </div>
        </div>
      );
    }

    return (
      <ResponsiveContainer width="100%" height={300}>
        <PieChart>
          <Pie
            data={errorData}
            cx="50%"
            cy="50%"
            labelLine={false}
            label={(props) => {
              const { payload } = props; // payload is your original data
              return `${getStatusCodeInfo(payload.status_code).name}: ${payload.count} (${safePercentage(payload.percentage).toFixed(1)}%)`;
            }}
            outerRadius={80}
            fill="#8884d8"
            dataKey="count"
          >
            {errorData.map((entry, index) => (
              <Cell
                key={`cell-${index}`}
                fill={ERROR_COLORS[index % ERROR_COLORS.length]}
              />
            ))}
          </Pie>
          <RechartsTooltip
            contentStyle={{
              backgroundColor: 'white',
              border: '1px solid #ccc',
              borderRadius: '6px',
              fontSize: '12px',
            }}
            formatter={(value: any) => [`${value} errors`, 'Count']}
            labelFormatter={(label) => `Status: ${label}`}
          />
        </PieChart>
      </ResponsiveContainer>
    );
  };

  const renderErrorSummary = () => {
    return (
      <div className="grid gap-4">
        {errorData.map((errorItem, index) => {
          const statusInfo = getStatusCodeInfo(errorItem.status_code);
          return (
            <div
              key={errorItem.status_code}
              className="flex items-center justify-between p-4 border rounded-lg"
            >
              <div className="flex items-center gap-4">
                <div
                  className="w-4 h-4 rounded-full"
                  style={{
                    backgroundColor: ERROR_COLORS[index % ERROR_COLORS.length],
                  }}
                />
                <div>
                  <h4 className="font-medium">HTTP {errorItem.status_code}</h4>
                  <p className="text-sm text-gray-600">{statusInfo.name}</p>
                  <p className="text-xs text-gray-500">
                    {statusInfo.description}
                  </p>
                </div>
              </div>
              <div className="text-right">
                <p className="text-lg font-bold">{errorItem.count}</p>
                <p className="text-sm text-gray-500">
                  {safePercentage(errorItem.percentage).toFixed(1)}%
                </p>
              </div>
            </div>
          );
        })}
      </div>
    );
  };

  const renderRecentErrors = () => {
    if (!recentErrors.length) {
      return (
        <div className="text-center py-8 text-gray-500">
          <p className="text-sm font-medium mb-2">No recent errors</p>
          <p className="text-xs text-gray-400">
            Recent error logs will appear here
          </p>
        </div>
      );
    }

    return (
      <div className="overflow-x-auto">
        <Table aria-label="Recent errors table" className="min-w-full">
          <TableHeader>
            <TableColumn>Time</TableColumn>
            <TableColumn>Endpoint</TableColumn>
            <TableColumn>Method</TableColumn>
            <TableColumn>Status</TableColumn>
            <TableColumn>Error</TableColumn>
            <TableColumn>Model</TableColumn>
          </TableHeader>
          <TableBody>
            {recentErrors.map((errorItem, index) => {
              const statusInfo = getStatusCodeInfo(errorItem.status_code);
              return (
                <TableRow key={`${errorItem.created_at}-${index}`}>
                  <TableCell>
                    <div className="text-xs">
                      {new Date(errorItem.created_at).toLocaleString()}
                    </div>
                  </TableCell>
                  <TableCell>
                    <p
                      className="text-sm font-medium truncate"
                      title={errorItem.endpoint}
                    >
                      {formatEndpoint(errorItem.endpoint)}
                    </p>
                  </TableCell>
                  <TableCell>
                    <Chip size="sm" variant="flat">
                      {errorItem.method}
                    </Chip>
                  </TableCell>
                  <TableCell>
                    <Chip color={statusInfo.color} size="sm" variant="flat">
                      {errorItem.status_code}
                    </Chip>
                  </TableCell>
                  <TableCell>
                    <Tooltip
                      content={
                        errorItem.error_message || 'No error message available'
                      }
                    >
                      <p className="text-sm truncate max-w-xs">
                        {errorItem.error_message || 'No message'}
                      </p>
                    </Tooltip>
                  </TableCell>
                  <TableCell>
                    <p className="text-xs text-gray-500">
                      {errorItem.model_used || 'Unknown'}
                    </p>
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </div>
    );
  };

  if (loading) {
    return (
      <Card className={className}>
        <CardHeader>
          <h3 className="text-lg font-semibold">Error Analysis</h3>
        </CardHeader>
        <CardBody className="flex items-center justify-center h-64">
          <Spinner size="lg" />
        </CardBody>
      </Card>
    );
  }

  return (
    <Card className={className}>
      <CardHeader>
        <div className="flex justify-between items-center w-full">
          <div className="flex items-center gap-2">
            <Icon
              icon={FaExclamationTriangle}
              className="w-5 h-5 text-warning"
            />
            <h3 className="text-lg font-semibold">Error Analysis</h3>
          </div>
          <div className="flex items-center gap-2">
            <Tooltip content="Refresh error data">
              <Button
                isIconOnly
                variant="light"
                size="sm"
                onPress={loadErrorData}
              >
                <Icon icon={FaArrowsRotate} className="w-4 h-4" />
              </Button>
            </Tooltip>
            <Tooltip content="Error analysis helps identify common issues and patterns in API usage">
              <Button isIconOnly variant="light" size="sm">
                <Icon icon={FaInfoCircle} className="w-4 h-4 text-gray-400" />
              </Button>
            </Tooltip>
          </div>
        </div>
      </CardHeader>
      <CardBody>
        {error ? (
          <div className="text-center text-red-500 py-8">
            <p>{error}</p>
            <Button
              color="primary"
              variant="flat"
              className="mt-4"
              onPress={loadErrorData}
            >
              Retry
            </Button>
          </div>
        ) : (
          <Tabs defaultSelectedKey="distribution" className="w-full">
            <Tab
              key="distribution"
              title={`Error Distribution (${errorData.length} types)`}
            >
              <div className="mt-4">
                {errorData.length > 0 ? renderErrorDistribution() : null}
                {errorData.length > 0 && (
                  <div className="mt-6">{renderErrorSummary()}</div>
                )}
                {errorData.length === 0 && (
                  <div className="flex flex-col items-center justify-center py-8 text-gray-500">
                    <div className="text-center">
                      <p className="text-sm font-medium mb-2">
                        No errors recorded
                      </p>
                      <p className="text-xs text-gray-400">
                        Your API usage is error-free!
                      </p>
                    </div>
                  </div>
                )}
              </div>
            </Tab>
            <Tab key="recent" title={`Recent Errors (${recentErrors.length})`}>
              <div className="mt-4">{renderRecentErrors()}</div>
            </Tab>
          </Tabs>
        )}
      </CardBody>
    </Card>
  );
};

export default ErrorAnalysisCard;
